// Ops dashboard — WOs the techs marked done; the PM reads the close-out, acknowledges, then closes the WO out from its screen.
import { NoteText } from "@/i18n/NoteText";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, ExternalLink, FileText, PackageOpen, RotateCcw, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { getFieldCompletions, unacknowledgeFieldCompletion } from "@/lib/api";
import { confirmFieldCompletion } from "@/lib/fieldCompletion";
import { BTN } from "@/components/common/btn";
import { useSectionCollapse } from "@/components/common/ExpandCollapseAll";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

export default function FieldCompletionsPanel() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [showAcked, setShowAcked] = useState(false);
  const [busy, setBusy] = useState(null);
  const [collapsed] = useSectionCollapse("fieldCompletions");

  // Always fetch acknowledged rows too, so the history toggle stays reachable once everything is acknowledged.
  const load = useCallback(async () => {
    try { setRows((await getFieldCompletions(true)).data.rows || []); } catch { /* panel stays as-is */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ack = async (wo, undo = false) => {
    setBusy(wo.id);
    try {
      if (undo) await unacknowledgeFieldCompletion(wo.id); else await confirmFieldCompletion(wo.id);
      toast.success(undo ? `${wo.wo_number} back in review` : `${wo.wo_number} confirmed — completed and archived`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(null); }
  };
  const openWO = (wo) => navigate(`/work-orders/${wo.id}`);

  const pending = rows.filter(r => !r.acknowledged).length;
  const visible = showAcked ? rows : rows.filter(r => !r.acknowledged);
  return (
    <div className="priority-list border-emerald-200" data-testid="field-completions-panel">
      <div className="priority-list-header">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        <h3 className="priority-list-title">Field Completions</h3>
        {pending > 0 && <span className="ml-2 text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="field-completions-count">{pending}</span>}
        {rows.length > pending && (
          <button type="button" className="ml-auto text-[11px] text-slate-500 hover:underline" onClick={() => setShowAcked(v => !v)} data-testid="field-completions-toggle-acked">
            {showAcked ? "Hide acknowledged" : `Show acknowledged (${rows.length - pending})`}
          </button>
        )}
      </div>
      {!collapsed && (
      <div className="dash-scroll">
        {visible.length === 0 && <div className="px-4 py-3 text-xs text-slate-400" data-testid="field-completions-empty">{rows.length ? `Nothing waiting for review — ${rows.length} acknowledged, still open until marked Completed.` : "No work orders marked done by a tech."}</div>}
        {visible.map(wo => <FieldCompletionRow key={wo.id} wo={wo} busy={busy === wo.id} onAck={() => ack(wo)} onUndo={() => ack(wo, true)} onOpen={() => openWO(wo)} />)}
      </div>
      )}
    </div>
  );
}

function FieldCompletionRow({ wo, busy, onAck, onUndo, onOpen }) {
  const [sig, setSig] = useState(false);
  return (
    <div className={`priority-item flex-col items-start gap-2 ${wo.acknowledged ? "opacity-70" : ""}`} data-testid={`field-completion-${wo.id}`}>
      <div className="flex items-start justify-between w-full gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={onOpen} className="text-sm font-semibold text-slate-800 hover:underline font-mono" data-testid={`field-completion-wo-${wo.id}`}>{wo.wo_number}</button>
            <span className="text-sm text-slate-700 truncate">{wo.title}</span>
            <span className="text-xs text-slate-500">{wo.client_name}{wo.site_name ? ` · ${wo.site_name}` : ""}</span>
            {wo.acknowledged && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid={`field-completion-acked-${wo.id}`}>Acknowledged by {wo.field_complete_ack?.by} · {when(wo.field_complete_ack?.at)}</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{wo.field_completed_by || "technician"}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{when(wo.field_completed_at)}</span>
            {wo.field_completion_hours != null && <span>{wo.field_completion_hours} h logged</span>}
            {wo.completion_signed_by && <span>Signed by {wo.completion_signed_by}</span>}
            <span className="flex items-center gap-1" title="Invoices on this WO"><FileText className="w-3 h-3" />{wo.invoice_count} inv</span>
            <span className={`flex items-center gap-1 ${wo.open_po_count ? "text-amber-700" : ""}`} title="POs not yet received"><PackageOpen className="w-3 h-3" />{wo.open_po_count} open PO</span>
            {wo.project_manager && <span>PM {wo.project_manager}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onOpen} data-testid={`field-completion-open-${wo.id}`}>
            <ExternalLink className="w-3 h-3 mr-1" /> Open WO
          </Button>
          {wo.acknowledged ? (
            <Button variant="ghost" size="sm" className="h-7 text-xs" disabled={busy} onClick={onUndo} data-testid={`field-completion-undo-${wo.id}`}><RotateCcw className="w-3 h-3 mr-1" /> Undo</Button>
          ) : (
            <Button size="sm" className={`${BTN.write} h-7 text-xs`} disabled={busy} onClick={onAck} data-testid={`field-completion-ack-${wo.id}`}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Confirm
            </Button>
          )}
        </div>
      </div>
      <div className="w-full rounded border border-slate-200 bg-white p-2 text-xs text-slate-700 whitespace-pre-wrap" data-testid={`field-completion-note-${wo.id}`}>
        {wo.completion_note?.trim() ? <NoteText note={{ text: wo.completion_note, text_original: wo.completion_note_original, text_lang: wo.completion_note_lang }} /> : <span className="italic text-slate-400">No close-out note.</span>}
      </div>
      {wo.completion_signature && (
        <button type="button" onClick={() => setSig(s => !s)} className="text-[11px] text-slate-500 hover:underline" data-testid={`field-completion-sig-toggle-${wo.id}`}>
          {sig ? "Hide signature" : "Show signature"}
        </button>
      )}
      {sig && wo.completion_signature && <img src={wo.completion_signature} alt="signature" className="h-14 bg-white border rounded px-2" data-testid={`field-completion-sig-${wo.id}`} />}
    </div>
  );
}
